import React from 'react'

const Chip = React.forwardRef(
  (
    {
      children,
      variant = 'default',
      color = 'default',
      size = 'medium',
      disabled = false,
      clickable = true,
      deletable = false,
      onDelete,
      onClick,
      avatar,
      icon,
      label,
      sx = {},
      className = '',
      ...props
    },
    ref
  ) => {
    // Get chip styles based on variant, color, and size
    const getChipStyles = () => {
      const baseStyles = {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        boxSizing: 'border-box',
        outline: 0,
        border: 0,
        margin: 0,
        cursor: clickable && !disabled ? 'pointer' : 'default',
        userSelect: 'none',
        verticalAlign: 'middle',
        textDecoration: 'none',
        fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
        fontWeight: 400,
        lineHeight: 1.2,
        letterSpacing: '0.01071em',
        borderRadius: '16px',
        transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1) 0ms',
        '&:hover':
          clickable && !disabled
            ? {
                backgroundColor: 'rgba(0, 0, 0, 0.04)'
              }
            : {},
        '&:active':
          clickable && !disabled
            ? {
                boxShadow:
                  '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)'
              }
            : {}
      }

      // Size variations
      const sizeStyles = {
        small: {
          height: '24px',
          fontSize: '0.75rem',
          padding: '0 8px'
        },
        medium: {
          height: '32px',
          fontSize: '0.8125rem',
          padding: '0 12px'
        },
        large: {
          height: '40px',
          fontSize: '0.875rem',
          padding: '0 16px'
        }
      }

      // Color variations
      const colorStyles = {
        default: {
          backgroundColor: '#e0e0e0',
          color: 'rgba(0, 0, 0, 0.87)'
        },
        primary: {
          backgroundColor: '#e3f2fd',
          color: '#1976d2'
        },
        secondary: {
          backgroundColor: '#f3e5f5',
          color: '#9c27b0'
        },
        error: {
          backgroundColor: '#ffebee',
          color: '#d32f2f'
        },
        warning: {
          backgroundColor: '#fff3e0',
          color: '#ed6c02'
        },
        info: {
          backgroundColor: '#e1f5fe',
          color: '#0288d1'
        },
        success: {
          backgroundColor: '#e8f5e8',
          color: '#2e7d32'
        }
      }

      // Variant styles
      const variantStyles = {
        default: {
          ...colorStyles[color]
        },
        outlined: {
          backgroundColor: 'transparent',
          color: colorStyles[color].color,
          border: `1px solid ${colorStyles[color].color}`,
          '&:hover':
            clickable && !disabled
              ? {
                  backgroundColor: colorStyles[color].backgroundColor
                }
              : {}
        },
        filled: {
          ...colorStyles[color]
        }
      }

      // Disabled styles
      const disabledStyles = disabled
        ? {
            opacity: 0.38,
            cursor: 'default',
            pointerEvents: 'none'
          }
        : {}

      return {
        ...baseStyles,
        ...sizeStyles[size],
        ...variantStyles[variant],
        ...disabledStyles,
        ...sx
      }
    }

    const getAvatarStyles = () => {
      const sizeStyles = {
        small: {
          width: '18px',
          height: '18px',
          marginRight: '6px'
        },
        medium: {
          width: '24px',
          height: '24px',
          marginRight: '8px'
        },
        large: {
          width: '32px',
          height: '32px',
          marginRight: '12px'
        }
      }

      return {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        overflow: 'hidden',
        ...sizeStyles[size]
      }
    }

    const getIconStyles = () => {
      const sizeStyles = {
        small: {
          width: '16px',
          height: '16px',
          marginRight: '6px'
        },
        medium: {
          width: '20px',
          height: '20px',
          marginRight: '8px'
        },
        large: {
          width: '24px',
          height: '24px',
          marginRight: '12px'
        }
      }

      return {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'inherit',
        ...sizeStyles[size]
      }
    }

    const getDeleteIconStyles = () => {
      const sizeStyles = {
        small: {
          width: '16px',
          height: '16px',
          marginLeft: '6px',
          cursor: 'pointer',
          '&:hover': {
            color: 'rgba(0, 0, 0, 0.6)'
          }
        },
        medium: {
          width: '18px',
          height: '18px',
          marginLeft: '8px',
          cursor: 'pointer',
          '&:hover': {
            color: 'rgba(0, 0, 0, 0.6)'
          }
        },
        large: {
          width: '20px',
          height: '20px',
          marginLeft: '12px',
          cursor: 'pointer',
          '&:hover': {
            color: 'rgba(0, 0, 0, 0.6)'
          }
        }
      }

      return {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'inherit',
        ...sizeStyles[size]
      }
    }

    const styles = getChipStyles()
    const avatarStyles = getAvatarStyles()
    const iconStyles = getIconStyles()
    const deleteIconStyles = getDeleteIconStyles()

    const handleClick = (event) => {
      if (clickable && !disabled && onClick) {
        onClick(event)
      }
    }

    const handleDelete = (event) => {
      event.stopPropagation()
      if (deletable && !disabled && onDelete) {
        onDelete(event)
      }
    }

    const displayLabel = label ?? children

    return (
      <span
        ref={ref}
        className={className}
        style={styles}
        onClick={handleClick}
        role={clickable ? 'button' : undefined}
        tabIndex={clickable && !disabled ? 0 : undefined}
        {...props}
      >
        {avatar && <span style={avatarStyles}>{avatar}</span>}
        {icon && <span style={iconStyles}>{icon}</span>}
        <span className='chip-label'>{displayLabel}</span>
        {deletable && (
          <span
            style={deleteIconStyles}
            onClick={handleDelete}
            role='button'
            tabIndex={0}
            aria-label='delete'
          >
            ×
          </span>
        )}
      </span>
    )
  }
)

Chip.displayName = 'Chip'

export default Chip
